
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AdminHeader from '@/components/admin/AdminHeader';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { supabase } from '@/integrations/supabase/client';
import { applyRealtimeConfig } from '@/utils/realtimeConfig';
import { Badge } from '@/components/ui/badge';
import { Loader2, Menu, RefreshCw, Terminal } from 'lucide-react';
import VoteDiagnosticTool from '@/components/social/VoteDiagnosticTool';
import { useIsMobile } from '@/hooks/use-mobile';

// Define a type for channel status info
interface ChannelInfo {
  topic: string;
  state: string;
}

const CommentsFeedback = () => {
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [isSubscribedToDebug, setIsSubscribedToDebug] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isMobile = useIsMobile();

  // Effect to handle sidebar state on mobile/desktop switch
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Refresh channels list
  const refreshChannels = () => {
    const existingChannels = supabase.getChannels();
    setChannels(existingChannels.map(c => ({
      topic: c.topic,
      state: c.state
    })));
  };

  // Check channels periodically
  useEffect(() => {
    refreshChannels();
    const intervalId = setInterval(refreshChannels, 3000);
    return () => clearInterval(intervalId);
  }, []);

  // Handle applying config
  const handleApplyConfig = async () => {
    setIsLoading(true);
    try {
      const result = await applyRealtimeConfig();
      
      if (result.success) {
        refreshChannels();
      }
    } catch (error) {
      console.error('Error applying realtime configuration');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove all channels
  const removeAllChannels = () => {
    const existingChannels = supabase.getChannels();
    existingChannels.forEach(channel => {
      supabase.removeChannel(channel);
    });
    
    // Reset events and update channel list
    setEvents([]);
    setTimeout(refreshChannels, 500);
  };

  // Subscribe to debug channel
  const subscribeToDebugChannel = () => {
    if (isSubscribedToDebug) {
      return;
    }

    const debug_channel = supabase
      .channel('debug-channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'poll_comment_votes'
        },
        (payload) => {
          console.log('Debug channel received event:', payload);
          setEvents(prev => [{
            time: new Date().toISOString(),
            event_type: payload.eventType,
            table: payload.table,
            data: JSON.stringify(payload.new || payload.old || {}),
            id: Math.random().toString(36).substring(2)
          }, ...prev].slice(0, 20)); // Keep last 20 events
        }
      )
      .subscribe((status) => {
        console.log('Debug channel status:', status);
        if (status === 'SUBSCRIBED') {
          setIsSubscribedToDebug(true);
        } else if (status === 'CHANNEL_ERROR') {
          setIsSubscribedToDebug(false);
        }
      });
  };

  return (
    <div className="flex min-h-screen bg-nation-purple/10 dark:bg-gray-900/90">
      {/* Mobile sidebar toggle - handled in AdminSidebar */}
      <div className={`${sidebarOpen ? "block" : "hidden"} ${isMobile ? "fixed inset-0 z-50 bg-black/50" : ""}`} onClick={isMobile ? () => setSidebarOpen(false) : undefined}>
        <div onClick={e => isMobile && e.stopPropagation()} className={isMobile ? "h-full w-64" : ""}>
          <AdminSidebar onClose={isMobile ? () => setSidebarOpen(false) : undefined} />
        </div>
      </div>
      
      <div className="flex-1">
        <AdminHeader>
          {isMobile && (
            <Button
              variant="outline"
              size="icon"
              className="mr-2"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
          )}
          Comments & Votes Monitoring
        </AdminHeader>
        
        <main className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 gap-6">
              {/* Vote Diagnostic Tool */}
              <VoteDiagnosticTool contentType="poll" />
              
              {/* Channel Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Active Channels</span>
                    <Button variant="outline" size="sm" onClick={refreshChannels}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    Currently active Supabase realtime channels
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {channels.length === 0 ? (
                      <p className="text-muted-foreground">No active channels</p>
                    ) : (
                      channels.map((channel, i) => (
                        <div key={i} className="flex items-center justify-between p-2 border rounded-md">
                          <span className="font-mono text-sm">{channel.topic}</span>
                          <Badge variant={channel.state === 'joined' ? 'secondary' : 'outline'}>
                            {channel.state}
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <div className="mt-4 space-x-2">
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={removeAllChannels}
                    >
                      Remove All Channels
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Apply Config */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Terminal className="h-5 w-5 mr-2" />
                    Apply Realtime Configuration
                  </CardTitle>
                  <CardDescription>
                    Apply Supabase realtime configuration for vote tables
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground mb-2">
                      Apply database configuration to ensure realtime updates work properly. 
                      This will set proper REPLICA IDENTITY settings and add tables to publications.
                    </p>
                    
                    <Button
                      onClick={handleApplyConfig}
                      disabled={isLoading}
                      className="flex items-center gap-2"
                    >
                      {isLoading ? 
                        <Loader2 className="h-4 w-4 animate-spin" /> : 
                        <Terminal className="h-4 w-4" />
                      }
                      Apply Configuration
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Debug Events */}
              <Card>
                <CardHeader>
                  <CardTitle>Vote Events</CardTitle>
                  <CardDescription>
                    Monitor realtime vote events from the poll_comment_votes table
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <Button
                      onClick={subscribeToDebugChannel}
                      disabled={isSubscribedToDebug}
                      variant="outline"
                    >
                      {isSubscribedToDebug ? 'Monitoring Vote Events' : 'Start Monitoring Vote Events'}
                    </Button>
                  </div>
                  
                  <div className="mt-4 max-h-96 overflow-auto border rounded-md divide-y">
                    {events.length === 0 ? (
                      <p className="text-muted-foreground p-4">No events received yet</p>
                    ) : (
                      events.map((event) => (
                        <div key={event.id} className="p-2 text-sm">
                          <div className="font-semibold">{event.event_type} - {event.time}</div>
                          <div className="font-mono text-xs mt-1 whitespace-pre-wrap break-all">
                            {event.data}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CommentsFeedback;
